import { z } from 'zod';

import { IdSchema, IsoDateSchema } from './common.js';
import { ExperienceLevelSchema } from './onboarding.js';
import { PlanContentV1Schema, SessionTypeEnum } from './plan.js';

// ─── Enums ────────────────────────────────────────────────────────────────

export const RoutineArchetypeEnum = z.enum([
  'ppl',
  'bro_split',
  'upper_lower',
  'full_body',
  'push_pull',
  'arnold_split',
]);
export type RoutineArchetype = z.infer<typeof RoutineArchetypeEnum>;

// ─── RoutineTemplate (catalog row, post-load) ─────────────────────────────

export const RoutineSplitDayV1Schema = z.object({
  dayLabel: z.string().min(1).max(80),
  targetMuscleGroups: z.array(z.string().min(1)).min(1).max(8),
  sessionTypeHint: SessionTypeEnum,
});
export type RoutineSplitDayV1 = z.infer<typeof RoutineSplitDayV1Schema>;

export const RoutineSplitTemplateV1Schema = z.object({
  version: z.literal('v1'),
  weeklyOutline: z.array(RoutineSplitDayV1Schema).min(1).max(7),
});
export type RoutineSplitTemplateV1 = z.infer<typeof RoutineSplitTemplateV1Schema>;

export const RoutineTemplateV1Schema = z.object({
  version: z.literal('v1'),
  id: z.string().min(1).max(80),
  name: z.string().min(1).max(120),
  archetype: RoutineArchetypeEnum,
  description: z.string().min(1).max(500),
  daysPerWeekMin: z.number().int().min(1).max(7),
  daysPerWeekMax: z.number().int().min(1).max(7),
  experienceLevels: z.array(ExperienceLevelSchema).min(1),
  goalTypes: z.array(z.string().min(1)).min(1),
  equipmentRequired: z.array(z.string().min(1)).min(1),
  splitTemplate: RoutineSplitTemplateV1Schema,
});
export type RoutineTemplateV1 = z.infer<typeof RoutineTemplateV1Schema>;
export const RoutineTemplateCatalogSchema = z.object({ items: z.array(RoutineTemplateV1Schema) });
export type RoutineTemplateCatalog = z.infer<typeof RoutineTemplateCatalogSchema>;

// ─── RoutineRecommendation (one candidate, post server-enrichment) ────────

// Suitability is server-clamped to [50,99]; no two candidates in a batch may
// share the same score (Layer B nudges duplicates by ±1).
export const RoutineRecommendationV1Schema = z.object({
  version: z.literal('v1'),
  id: IdSchema,
  templateId: z.string().min(1).max(80),
  templateName: z.string().min(1).max(120),
  archetype: RoutineArchetypeEnum,
  suitabilityPct: z.number().int().min(50).max(99),
  rationale: z.string().min(20).max(500),
  adaptedContent: PlanContentV1Schema,
});
export type RoutineRecommendationV1 = z.infer<typeof RoutineRecommendationV1Schema>;

// ─── RoutineRecommendationBatch (DB row + response shape) ─────────────────

export const RoutineRecommendationBatchV1Schema = z.object({
  version: z.literal('v1'),
  id: IdSchema,
  userId: IdSchema,
  candidates: z.array(RoutineRecommendationV1Schema).length(3),
  regenCount: z.number().int().min(0).max(1),
  canRegen: z.boolean(),
  modelUsed: z.string().min(1),
  promptVersion: z.string().min(1),
  createdAt: IsoDateSchema,
});
export type RoutineRecommendationBatchV1 = z.infer<typeof RoutineRecommendationBatchV1Schema>;

// ─── API request / response DTOs ──────────────────────────────────────────

export const RecommendRoutinesResponseSchema = z.object({
  batch: RoutineRecommendationBatchV1Schema,
});
export type RecommendRoutinesResponse = z.infer<typeof RecommendRoutinesResponseSchema>;

export const GetRecommendationsResponseSchema = z.object({
  batch: RoutineRecommendationBatchV1Schema.nullable(),
});
export type GetRecommendationsResponse = z.infer<typeof GetRecommendationsResponseSchema>;

export const SelectRoutineRequestSchema = z.object({
  candidateId: IdSchema,
});
export type SelectRoutineRequest = z.infer<typeof SelectRoutineRequestSchema>;

export const SelectRoutineResponseSchema = z.object({
  planId: IdSchema,
});
export type SelectRoutineResponse = z.infer<typeof SelectRoutineResponseSchema>;
